import React, { useEffect } from 'react';

import LinkingError from 'microsites/partner-linking/linking-error';
import { useBackToApp } from 'microsites/partner-linking/use-back-to-app';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import logger from 'utils/logger';

/**
 * Partner-agnostic error page for the partnership-linking flows.
 *
 * Mounted at `/partner-linking/error` in `microsites/router`.
 * The CT flows `navigate()` here with a router location state of
 * `{ reason, detail, isAppOrigin }`:
 *   - `reason` — stable, non-localized reason code for the failure (e.g.
 *     `SESSION_EXPIRED`, `MISSING_CODE_OR_STATE`, `COMPLETE_ERROR`). NEVER
 *     rendered — every failure shows the single generic "Something went
 *     wrong" copy; `reason` exists only so Datadog can tell the branches
 *     apart. Absent on a direct visit / refresh that drops router state.
 *   - `detail` — raw/internal diagnostic context (err.message, CT
 *     error_description, callback-state notes). NEVER rendered — it's
 *     untranslated and may leak internals; logged to Datadog instead.
 *   - `isAppOrigin` — drives the "Back to Tim Hortons" CTA (app deep link vs
 *     web home); defaults to web home (safe on desktop — a deep link would
 *     no-op without the app). Only a confirmed app entry deep-links.
 */
interface IPartnerLinkingErrorState {
  reason?: string;
  detail?: string;
  isAppOrigin?: boolean;
}

export default function PartnerLinkingErrorPage(): JSX.Element {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const state = (location.state ?? {}) as IPartnerLinkingErrorState;
  const { reason, detail } = state;
  const handleBack = useBackToApp(state.isAppOrigin ?? false);

  // Log the reason code + raw diagnostic detail (neither shown to the user) so
  // ops keep the partner/gateway context in Datadog. Fires once per render.
  useEffect(() => {
    logger.error({
      message: 'partner-linking error screen shown',
      partnerLinkingErrorReason: reason ?? 'UNKNOWN',
      partnerLinkingErrorDetail: detail ?? null,
      // Opt out of the session-based Datadog log sampling
      // (`datadogLogSampleRate`); otherwise this low-volume error is dropped in
      // the ~99% of sessions sampling excludes and never reaches Datadog.
      bypassSampling: true,
    });
  }, [reason, detail]);

  // Users always see the single generic screen; the specific reason lives in
  // Datadog only.
  return (
    <LinkingError
      message={formatMessage({ id: 'partnerLinkingErrorGeneric' })}
      onBack={handleBack}
    />
  );
}
